import type { DateValidationError, PickerChangeHandlerContext } from "@mui/x-date-pickers/models";

import { extractOmnisearchValue, useOmnisearchState } from "hooks/omnisearch-v2";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";

import { PERIOD_FILTER_KEY, readPeriodFilter, writePeriodFilter } from "./periodFilter";

export const DEFAULT_PERIOD_MONTHS = 3;

function defaultStartDate(): DateTime {
  return DateTime.now().minus({ months: DEFAULT_PERIOD_MONTHS }).startOf("month");
}

function formatDateForApi(value: DateTime | null): string | undefined {
  return value?.isValid ? value.toFormat("yyyy-MM-dd") : undefined;
}

export function usePeriodFilter() {
  const { omnisearch, editOmnisearchKeyValues, removeOmnisearchKeyValue } = useOmnisearchState();
  const defaultStart = useMemo(defaultStartDate, []);
  const value = extractOmnisearchValue(omnisearch, PERIOD_FILTER_KEY);
  const [startDate, endDate] = useMemo(
    () => readPeriodFilter(value, defaultStart),
    [value, defaultStart]
  );

  const update = useCallback(
    (start: DateTime | null, end: DateTime | null) => {
      const nextValue = writePeriodFilter(start, end, defaultStart);
      if (nextValue) editOmnisearchKeyValues([[PERIOD_FILTER_KEY, nextValue]]);
      else removeOmnisearchKeyValue(PERIOD_FILTER_KEY);
    },
    [defaultStart, editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );
  const onStartChange = useCallback(
    (start: DateTime | null, context: PickerChangeHandlerContext<DateValidationError>) => {
      if (context.validationError != null || (start && !start.isValid)) return;
      update(start, endDate);
    },
    [endDate, update]
  );
  const onEndChange = useCallback(
    (end: DateTime | null, context: PickerChangeHandlerContext<DateValidationError>) => {
      if (context.validationError != null || (end && !end.isValid)) return;
      update(startDate, end);
    },
    [startDate, update]
  );

  return {
    startDate,
    endDate,
    startDateStr: formatDateForApi(startDate),
    endDateStr: formatDateForApi(endDate),
    onStartChange,
    onEndChange,
  };
}
